import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { clientesApi, locaisApi, mesasApi, reservasApi } from '../services/api';
import type { Cliente, Local, MapaMesa, MapaMesasResponse, Mesa } from '../types';

/**
 * Dados do mapa com cache leve stale-while-revalidate por data.
 * - Mostra cache imediato ao mudar de data, revalida em background.
 * - Expõe reload debounced para eventos SignalR (evita full-reload em rajada).
 */
export function useMapaData(data: Date) {
  const [mapaData, setMapaData] = useState<MapaMesasResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);

  const cacheRef = useRef(new Map<string, MapaMesasResponse>());
  const dateKey = format(data, 'yyyy-MM-dd');
  const dateKeyRef = useRef(dateKey);
  dateKeyRef.current = dateKey;
  const debounceRef = useRef<number | null>(null);

  const loadMapa = useCallback(
    async (background = false, target?: Date) => {
      const key = format(target ?? new Date(dateKeyRef.current + 'T12:00:00'), 'yyyy-MM-dd');
      try {
        if (!background && !cacheRef.current.has(key)) setLoading(true);
        if (!background && cacheRef.current.has(key)) {
          setMapaData(cacheRef.current.get(key)!);
        }
        const response = await reservasApi.getMapaMesas(target ?? new Date(dateKeyRef.current + 'T12:00:00'));
        if (response.success && response.data) {
          cacheRef.current.set(key, response.data);
          // Só aplica se a data ainda for a pedida (evita race ao navegar dias)
          if (format(target ?? new Date(dateKeyRef.current + 'T12:00:00'), 'yyyy-MM-dd') === dateKeyRef.current) {
            setMapaData(response.data);
          }
        }
      } catch {
        // mantém cache em caso de falha; UI mostra último estado conhecido
      } finally {
        if (!background) setLoading(false);
      }
    },
    []
  );

  /** Reload com debounce para rajadas SignalR (250ms). */
  const reloadDebounced = useCallback(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void loadMapa(true);
    }, 250);
  }, [loadMapa]);

  const loadClientes = useCallback(async () => {
    try {
      const response = await clientesApi.getAll(undefined, 1, 200);
      if (response.success && response.data) {
        setClientes(response.data.items.filter((c) => !c.clienteEventual && !c.bloqueado));
      }
    } catch {
      // toast tratado pelo chamador
    }
  }, []);

  const loadMesas = useCallback(async () => {
    try {
      const response = await mesasApi.getAll();
      if (response.success && response.data) {
        setMesas(response.data.filter((m) => m.podeReservar));
      }
    } catch {
      // silencioso: mapa continua com cache
    }
  }, []);

  const loadLocais = useCallback(async () => {
    try {
      const r = await locaisApi.getAll();
      if (r.success && r.data) setLocais(r.data);
    } catch {
      // silencioso
    }
  }, []);

  useEffect(() => {
    void loadMapa(false, data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateKey]);

  useEffect(() => {
    void loadClientes();
    void loadMesas();
    void loadLocais();
  }, [loadClientes, loadMesas, loadLocais]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const invalidateMapaCache = useCallback((key?: string) => {
    if (key) cacheRef.current.delete(key);
    else cacheRef.current.clear();
  }, []);

  return {
    mapaData,
    loading,
    clientes,
    mesas,
    locais,
    loadMapa,
    reloadDebounced,
    loadClientes,
    loadMesas,
    loadLocais,
    invalidateMapaCache,
  };
}

export function useMesasFiltradas(
  mesas: MapaMesa[] | undefined,
  localFiltro: number | null,
  estadoFiltro: number | null,
  mesaSearch: string
) {
  return useMemo(() => {
    let list = mesas ?? [];
    if (localFiltro) list = list.filter((m) => m.localId === localFiltro);
    if (estadoFiltro !== null) list = list.filter((m) => m.estado === estadoFiltro);
    const q = mesaSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (m) => String(m.numero).includes(q) || m.descricao.toLowerCase().includes(q)
      );
    }
    return list;
  }, [mesas, localFiltro, estadoFiltro, mesaSearch]);
}

export function useLocaisComMesas(mesasFiltradas: MapaMesa[]) {
  return useMemo(() => {
    const porLocal = new Map<
      number,
      { localId: number; localDescricao: string; localCor: string; mesas: MapaMesa[] }
    >();
    mesasFiltradas.forEach((mesa) => {
      if (!porLocal.has(mesa.localId)) {
        porLocal.set(mesa.localId, {
          localId: mesa.localId,
          localDescricao: mesa.localDescricao,
          localCor: mesa.localCor,
          mesas: [],
        });
      }
      porLocal.get(mesa.localId)!.mesas.push(mesa);
    });
    return Array.from(porLocal.values());
  }, [mesasFiltradas]);
}
